import { useAdminContext } from '@/contexts/AdminContext';
import { useAuth } from '@/contexts/AuthContext';
import {
    CreditCard,
    Plug,
    Stack,
    User,
    Users,
    X
} from '@phosphor-icons/react';
import clsx from 'clsx';
import React, {
    useEffect,
    useState
} from 'react';
import { createPortal } from 'react-dom';
import BillingSettings from './components/BillingSettings';
import IntegrationsSettings from './components/IntegrationsSettings';
import MembersSettings from './components/MembersSettings';
import ProfileSettings from './components/ProfileSettings';
import WorkspaceSettings from './components/WorkspaceSettings';

export enum NavKey {
    profile = 'profile',
    brand = 'brand',
    usage = 'usage',
    billing = 'billing',
    integration = 'integration',
    workspaceSettings = 'workspace-settings',
    members = 'members',
}
// --- Helper Components ---

// --- Sub-Pages ---

// ProfileSettings imported from components/ProfileSettings

// BillingSettings imported from components/BillingSettings

// WorkspaceSettings imported from components/WorkspaceSettings

// --- Main Modal Component ---

const mapTab = (key: NavKey): string => {
    switch (key) {
        case NavKey.profile: return 'profile';
        case NavKey.integration: return 'integrations';
        case NavKey.billing: return 'billing';
        case NavKey.brand: return 'workspace-settings';
        case NavKey.workspaceSettings: return 'workspace-settings';
        case NavKey.members: return 'members';
        default: return 'profile';
    }
};

const SettingsModal: React.FC = () => {
    const { modal } = useAdminContext();
    const {integrations} = useAuth();
    const isOpen = modal.isModalOpen('profile');
    const modalConfig = modal.getModalConfig('profile');
    const activeTabFromConfig = (modalConfig?.activeTab as NavKey) || NavKey.profile;
    const initialTab = mapTab(activeTabFromConfig);

    const [activeTab, setActiveTab] = useState(initialTab);

    const onClose = () => modal.closeModal('profile');

    useEffect(() => {
        if (isOpen) {
            setActiveTab(initialTab);
        }
    }, [isOpen, initialTab]);

    if (!isOpen) return null;

    const menuItems = [
        {
            category: 'Account',
            items: [
                {
                    id: 'profile', label: 'Profile', icon: User, description: 'Personal information and security' 
                },
            ]
        },
        {
            category: 'Workspace',
            items: [
                {
                    id: 'workspace-settings', label: 'Workspace Settings', icon: Stack, description: 'Workspace details and preferences' 
                },
                {
                    id: 'integrations', label: 'Integrations', icon: Plug, description: 'Connect your tools to Bustly' 
                },
                {
                    id: 'members', label: 'Members', icon: Users, description: 'Manage team access' 
                },
                {
                    id: 'billing', label: 'Billing & Plans', icon: CreditCard, description: 'Manage subscription and invoices' 
                },
            ]
        }
    ];

    const allTabs = menuItems.flatMap(g => g.items);
    const currentTabInfo = allTabs.find(t => t.id === activeTab);

    const renderContent = () => {
        switch (activeTab) {
            case 'profile':
                return <ProfileSettings />;
            case 'workspace-settings':
                return <WorkspaceSettings />;
            case 'integrations':
                return (
                    <IntegrationsSettings
                        workspaceIntegrations={integrations}
                    />
                );
            case 'members':
                return <MembersSettings />;
            case 'billing':
                return <BillingSettings onClose={onClose} />;
            default:
                return <div className="p-4 text-gray-500">Coming Soon</div>;
        }
    };

    return createPortal(
        <div 
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/20 backdrop-blur-sm p-4"
            onClick={onClose}
        >
            <div 
                className="bg-white w-full max-w-5xl h-[80vh] rounded-2xl shadow-2xl flex overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Sidebar */}
                <div className="w-64 bg-gray-50/30 border-r border-gray-200 flex flex-col pt-6">
                    <div className="space-y-6 px-3 pb-6">
                        {menuItems.map((section) => (
                            <div key={section.category}>
                                <div className="text-xs font-bold text-text-sub/80 text-gray-400 tracking-wider px-2 mb-2 uppercase">
                                    {section.category}
                                </div>
                                <div className="space-y-[2px]">
                                    {section.items.map((item) => {
                                        const isActive = activeTab === item.id;
                                        const Icon = item.icon;
                                        
                                        return (
                                            <button
                                                key={item.id}
                                                onClick={() => setActiveTab(item.id)}
                                                className={clsx(
                                                    "w-full flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all",
                                                    isActive
                                                        ? "bg-gray-200 text-text-main text-gray-900"
                                                        : "text-text-sub text-gray-500 hover:bg-gray-100 hover:text-text-main hover:text-gray-900"
                                                )}
                                            >
                                                <Icon size={18} className="shrink-0" />
                                                <span className="flex-1 text-left truncate">{item.label}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 flex flex-col min-w-0 bg-white overflow-hidden relative">
                    {/* Close Button */}
                    <button
                        onClick={onClose}
                        className="absolute top-6 right-6 z-10 p-2 text-text-sub text-gray-400 hover:text-text-main hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X size={20} weight="bold" />
                    </button>

                    <div className="flex-1 overflow-y-auto custom-scrollbar">
                        <div className="max-w-4xl mx-auto px-8 py-6 pb-12 pr-16">
                            <div className="mb-8">
                                <h2 className="text-xl font-bold text-text-main text-gray-900">{currentTabInfo?.label}</h2>
                                <p className="text-text-sub text-gray-500 mt-1">{currentTabInfo?.description}</p>
                            </div>
                            {renderContent()}
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default SettingsModal;
